import DistributionCheckout from '../components/DistributionCheckout.jsx';
import DistributionModelSelector, { formatDistributionModels } from '../components/DistributionModelSelector.jsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, NavLink } from 'react-router-dom';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { filmmakerRequest, filmmakerUpload } from '../services/business/businessService.js';
import '../styles/business.css';

const DISTRIBUTION_TERMS_VERSION = '2026-08-22';
const navItems = [
  ['/filmmakers/dashboard', 'Dashboard'],
  ['/filmmakers/titles', 'Titles'],
  ['/filmmakers/uploads', 'Delivery'],
  ['/filmmakers/revenue', 'Revenue'],
  ['/filmmakers/payouts', 'Payouts'],
  ['/filmmakers/messages', 'Messages'],
  ['/filmmakers/orders', 'Orders'],
  ['/filmmakers/profile', 'Profile'],
];

function money(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : '$0.00';
}

function statusLabel(value) {
  return String(value || 'pending').replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function dateLabel(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
}

function periodLabel(report) {
  if (report?.period_label) return report.period_label;
  if (report?.period_start || report?.period_end) return `${dateLabel(report.period_start)} – ${dateLabel(report.period_end)}`;
  return dateLabel(report?.created_at);
}

function PortalShell({ title, subtitle, children }) {
  return (
    <section className="business-page filmmaker-page">
      <header className="business-hero filmmaker-hero">
        <span className="eyebrow">WatchVIM Filmmaker Portal</span>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </header>
      <nav className="business-subnav filmmaker-nav" aria-label="Filmmaker portal">
        {navItems.map(([to, label]) => <NavLink key={to} to={to}>{label}</NavLink>)}
      </nav>
      <div className="business-content">{children}</div>
    </section>
  );
}

function PortalMessage({ error, status, success }) {
  if (error) return <p className="business-message error" role="alert">{error}</p>;
  if (success) return <p className="business-message success" role="status">{success}</p>;
  if (status) return <p className="business-message" role="status">{status}</p>;
  return null;
}

function usePortalData() {
  const { ready, session, user } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!session?.access_token) return null;
    setLoading(true);
    setError('');
    try {
      const next = await filmmakerRequest('me', { session });
      setData(next);
      return next;
    } catch (err) {
      setError(err?.message || 'Filmmaker portal could not be loaded.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    if (session?.access_token) void refresh();
    else { setData(null); setLoading(false); }
  }, [session?.access_token, refresh]);

  return { ready, session, user, data, loading, error, refresh };
}

function PortalGate({ portal, children }) {
  const returnPath = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search || ''}` : '/filmmakers/dashboard';
  if (portal.ready && !portal.user) return <Navigate to={`/login?return=${encodeURIComponent(returnPath)}`} replace />;
  if (!portal.ready || portal.loading) return <PortalShell title="Filmmaker Portal" subtitle="Loading your filmmaker account…"><p className="business-message" aria-busy="true">Loading portal…</p></PortalShell>;
  if (portal.error && !portal.data) return <PortalShell title="Filmmaker Portal" subtitle="Your signed-in filmmaker workspace."><PortalMessage error={portal.error} /></PortalShell>;
  return children;
}

function StatCard({ label, value, detail }) {
  return <article className="business-card portal-stat"><span className="business-kicker">{label}</span><strong>{value}</strong>{detail ? <p>{detail}</p> : null}</article>;
}

function financeTotals(titles) {
  return (titles || []).reduce((totals, title) => ({
    earnings: totals.earnings + (Number(title.lifetime_earnings ?? title.total_revenue) || 0),
    available: totals.available + (Number(title.available_balance) || 0),
    pending: totals.pending + (Number(title.pending_payout) || 0),
    paid: totals.paid + (Number(title.lifetime_paid) || 0),
  }), { earnings: 0, available: 0, pending: 0, paid: 0 });
}

function TitleStatus({ title }) {
  return (
    <article className="business-card filmmaker-title-card">
      <div className="portal-card-heading">
        <div><span className="business-kicker">{statusLabel(title.status || title.ingest_status || 'active')}</span><h2>{title.title || 'Untitled'}</h2></div>
        <strong>{money(title.available_balance)} available</strong>
      </div>
      <p>{[title.content_type || title.type, title.release_year, title.genre].filter(Boolean).join(' • ') || 'WatchVIM filmmaker title'}</p>
      {formatDistributionModels(title.distribution_models) ? <p className="business-fineprint">Offer models: {formatDistributionModels(title.distribution_models)}</p> : null}
      <div className="portal-mini-stats">
        <span>Lifetime earnings <strong>{money(title.lifetime_earnings ?? title.total_revenue)}</strong></span>
        <span>Pending payout <strong>{money(title.pending_payout)}</strong></span>
        <span>Lifetime paid <strong>{money(title.lifetime_paid)}</strong></span>
      </div>
    </article>
  );
}

function Dashboard({ portal }) {
  const titles = portal.data?.titles || [];
  const pending = portal.data?.pending_submissions || portal.data?.submissions || [];
  const totals = financeTotals(titles);
  return (
    <PortalShell title="Filmmaker Dashboard" subtitle="Track titles, delivery, streaming performance, certified revenue, payouts, submissions, and WatchVIM messages from one workspace.">
      <div className="business-card-grid four">
        <StatCard label="Lifetime Earnings" value={money(totals.earnings)} detail="Certified filmmaker revenue" />
        <StatCard label="Available Balance" value={money(totals.available)} detail="Eligible for a payout request" />
        <StatCard label="Pending Payout" value={money(totals.pending)} detail="Requested, approved, or processing" />
        <StatCard label="Lifetime Paid" value={money(totals.paid)} detail="Completed payout history" />
      </div>
      <div className="business-two-column">
        <div className="portal-stack">
          <h2>Titles</h2>
          {titles.length ? titles.slice(0, 4).map((title) => <TitleStatus title={title} key={title.id} />) : <div className="business-card"><h2>No approved titles yet</h2><p>Submit a title from the Titles section for WatchVIM review.</p><NavLink to="/filmmakers/titles">Submit a Title</NavLink></div>}
        </div>
        <aside className="business-card sticky-card"><h2>Pending review</h2>{pending.length ? pending.slice(0, 6).map((item) => <div className="portal-list-row" key={item.id || item.title}><strong>{item.title}</strong><span>{statusLabel(item.status)}</span></div>) : <p>No pending submissions.</p>}</aside>
      </div>
    </PortalShell>
  );
}

function Titles({ portal }) {
  const titles = portal.data?.titles || [];
  const pending = portal.data?.pending_submissions || portal.data?.submissions || [];
  const [status, setStatus] = useState(''); const [error, setError] = useState(''); const [success, setSuccess] = useState(''); const [submitting, setSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  async function submit(event) {
    event.preventDefault();
    if (submissionInFlight.current) return;
    submissionInFlight.current = true;
    setSubmitting(true); setError(''); setSuccess(''); setStatus('Submitting title for review…');
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const distributionModels = [...new Set(
      form.getAll('distribution_models').map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
    )];
    if (!distributionModels.length) {
      setStatus('');
      setError('Choose at least one offer model: SVOD, TVOD, or AVOD.');
      submissionInFlight.current = false;
      setSubmitting(false);
      return;
    }
    const body = Object.fromEntries(form.entries());
    body.distribution_models = distributionModels;
    body.rights_confirm = form.get('rights_confirm') === 'on';
    body.agreed = form.get('agreed') === 'on';
    body.distribution_agreement_version = DISTRIBUTION_TERMS_VERSION;
    body.title_withdrawal_all_platforms_acknowledged = form.get('title_withdrawal_all_platforms_acknowledged') === 'on';
    body.final_earnings_paid_after_platform_collection_acknowledged = form.get('final_earnings_paid_after_platform_collection_acknowledged') === 'on';
    try {
      const result = await filmmakerRequest('submit-title', { session: portal.session, method: 'POST', body });
      const reference = result.submissionId || result.id || 'pending review';
      setSuccess(result.duplicate
        ? `This title is already under review. Existing reference: ${reference}. No duplicate was created.`
        : `Title submitted. Reference: ${reference}${result.confirmation_email?.warning ? ' ' + result.confirmation_email.warning : ''}`);
      formElement.reset();
      await portal.refresh();
    } catch (err) {
      setError(err?.message || 'Title submission failed.');
    } finally {
      setStatus('');
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  }
  return (
    <PortalShell title="Filmmaker Titles" subtitle="Review approved titles and submit new work directly from your signed-in filmmaker account.">
      {titles.length ? <div className="business-card-grid two">{titles.map((title) => <TitleStatus title={title} key={title.id} />)}</div> : <p className="business-muted">No approved titles are linked to this account yet.</p>}
      {pending.length ? <div className="business-card"><h2>Pending submissions</h2>{pending.map((item) => <div className="portal-list-row" key={item.id || item.title}><strong>{item.title}</strong><span>{statusLabel(item.status)}</span></div>)}</div> : null}
      <form className="business-form wide" onSubmit={submit}>
        <h2>Submit a new title</h2>
        <div className="business-field-row two"><label>Title<input name="title" required /></label><label>Content type<select name="content_type" defaultValue="film"><option value="film">Film</option><option value="series">Series</option><option value="short">Short</option><option value="documentary">Documentary</option></select></label></div>
        <div className="business-field-row three"><label>Release year<input name="release_year" inputMode="numeric" /></label><label>Runtime minutes<input name="runtime_mins" inputMode="numeric" /></label><label>Genre<input name="genre" /></label></div>
        <div className="business-field-row two"><label>Language<input name="language" /></label><label>Rating<input name="rating" /></label></div>
        <label>Synopsis<textarea name="synopsis" rows="5" /></label>
        <label>Secure screener URL<input name="screener_url" type="url" placeholder="https://" required /></label>
        <div className="business-field-row two"><label>Poster URL<input name="poster_url" type="url" placeholder="https://" /></label><label>Trailer URL<input name="trailer_url" type="url" placeholder="https://" /></label></div>
        <label>Additional links<textarea name="additional_links" rows="3" /></label>
        <DistributionModelSelector />
        <div className="business-card">
          <span className="business-kicker">Non-exclusive distribution terms</span>
          <h3>Withdrawal and final payout acknowledgement</h3>
          <p>A request to withdraw a title from WatchVIM also requests removal from every third-party platform to which WatchVIM / VIM Media distributed or delivered the title. Each third-party platform controls its own takedown timing, so removal may not be immediate.</p>
          <p>WatchVIM may retain the limited authority needed to process takedowns, reconcile outstanding revenue, refunds or chargebacks, and complete final reporting. Final filmmaker earnings are paid in the next applicable payout period after WatchVIM receives and clears the corresponding platform funds, less permitted deductions, refunds, chargebacks, or offsets.</p>
          <p className="business-fineprint">These terms do not make the distribution grant exclusive. Version {DISTRIBUTION_TERMS_VERSION}.</p>
        </div>
        <label className="business-check"><input name="rights_confirm" type="checkbox" required /> I confirm I control the rights needed for this submission.</label>
        <label className="business-check"><input name="title_withdrawal_all_platforms_acknowledged" type="checkbox" required /> I acknowledge that withdrawing my title from WatchVIM requests removal from every platform where WatchVIM / VIM Media distributed or delivered it, subject to each platform’s takedown timeline.</label>
        <label className="business-check"><input name="final_earnings_paid_after_platform_collection_acknowledged" type="checkbox" required /> I acknowledge that final earnings are paid in the next applicable payout period after WatchVIM receives and clears the applicable platform funds, less permitted deductions, refunds, chargebacks, or offsets.</label>
        <label className="business-check"><input name="agreed" type="checkbox" required /> I agree to the WatchVIM submission terms and the non-exclusive distribution terms shown above.</label>
        <DistributionCheckout session={portal.session} />
        <PortalMessage error={error} success={success} status={status} />
        <button className="business-primary button" type="submit" disabled={submitting}>{submitting ? 'Submitting…' : 'Submit for Review'}</button>
      </form>
    </PortalShell>
  );
}

function Revenue({ portal }) {
  const titles = portal.data?.titles || [];
  const rows = titles.flatMap((title) => (title.reports || []).map((report) => ({ title, report })));
  const totals = financeTotals(titles);
  const streamingRows = rows.map(({ title, report }) => {
    const analytics = report?.report_payload?.mux_analytics;
    if (!analytics || analytics.ok === false) return null;
    const summary = analytics.summary || {};
    return { title, report, analytics, summary };
  }).filter(Boolean);

  return (
    <PortalShell title="Filmmaker Revenue" subtitle="Streaming performance is shown separately from certified financial statements so playback analytics never silently change your balance.">
      <div className="business-card-grid four">
        <StatCard label="Lifetime Earnings" value={money(totals.earnings)} />
        <StatCard label="Available Balance" value={money(totals.available)} />
        <StatCard label="Pending Payout" value={money(totals.pending)} />
        <StatCard label="Lifetime Paid" value={money(totals.paid)} />
      </div>

      <div className="business-card portal-table-card">
        <div className="portal-card-heading"><div><span className="business-kicker">Analytics only</span><h2>WatchVIM Streaming Performance</h2></div><span className="business-muted">Does not change financial balances</span></div>
        {streamingRows.length ? <div className="portal-table-wrap"><table className="portal-table"><thead><tr><th>Title</th><th>Period</th><th>Streams</th><th>Unique Viewers</th><th>Watch Time</th><th>Top Market</th></tr></thead><tbody>{streamingRows.map(({ title, report, analytics, summary }, index) => {
          const countries = analytics?.dimensions?.countries || analytics?.dimensions?.country || [];
          const topMarket = Array.isArray(countries) && countries[0] ? countries[0].value || countries[0].name || '—' : '—';
          const watchSeconds = Number(summary.watchTimeSeconds || summary.totalWatchTimeSeconds || 0) || 0;
          return <tr key={`stream-${report.id || `${title.id}-${index}`}`}><td>{title.title}</td><td>{periodLabel(report)}</td><td>{Number(summary.views || report.streams || 0).toLocaleString()}</td><td>{Number(summary.uniqueViewers || 0).toLocaleString()}</td><td>{Math.round(watchSeconds / 60).toLocaleString()} min</td><td>{topMarket}</td></tr>;
        })}</tbody></table></div> : <p>No WatchVIM streaming-performance snapshots are attached to your current reports yet.</p>}
      </div>

      <div className="business-card portal-table-card">
        <div className="portal-card-heading"><div><span className="business-kicker">Financial ledger</span><h2>Revenue Statements</h2></div><span className="business-muted">Revenue and payout accounting</span></div>
        {rows.length ? <div className="portal-table-wrap"><table className="portal-table"><thead><tr><th>Title</th><th>Platform</th><th>Period</th><th>Qualified Streams</th><th>TVOD</th><th>Gross</th><th>Filmmaker 70%</th><th>Statement</th></tr></thead><tbody>{rows.map(({ title, report }, index) => <tr key={report.id || `${title.id}-${index}`}><td>{title.title}</td><td>{report.platform || 'WatchVIM'}</td><td>{periodLabel(report)}</td><td>{Number(report.streams || 0).toLocaleString()}</td><td>{Number(report.tvod_transactions || 0).toLocaleString()}</td><td>{money(report.gross_revenue)}</td><td><strong>{money(report.final_total_owed ?? report.filmmaker_total ?? report.net_to_filmmaker)}</strong></td><td>{report.report_pdf_url ? <a href={report.report_pdf_url} target="_blank" rel="noreferrer">Open PDF</a> : '—'}</td></tr>)}</tbody></table></div> : <p>No revenue statements are available yet.</p>}
      </div>
    </PortalShell>
  );
}

function Payouts({ portal }) {
  const titles = portal.data?.titles || [];
  const totals = financeTotals(titles);
  const [busy, setBusy] = useState(''); const [error, setError] = useState(''); const [success, setSuccess] = useState('');
  async function request(title) {
    setBusy(title.id); setError(''); setSuccess('');
    try { const result = await filmmakerRequest('payout-request', { session: portal.session, method: 'POST', body: { titleId: title.id } }); setSuccess(result.message || 'Payout request submitted.'); await portal.refresh(); }
    catch (err) { setError(err?.message || 'Payout request failed.'); }
    finally { setBusy(''); }
  }
  const allPayouts = titles.flatMap((title) => (title.payouts || []).map((payout) => ({ title, payout }))).sort((a, b) => String(b.payout.created_at || '').localeCompare(String(a.payout.created_at || '')));
  return (
    <PortalShell title="Filmmaker Payouts" subtitle="Requests are reviewed in the WatchVIM CMS. Paid payouts remain permanently visible in history while the available balance reflects the ledger automatically.">
      <PortalMessage error={error} success={success} />
      <div className="business-card-grid four">
        <StatCard label="Available" value={money(totals.available)} />
        <StatCard label="Pending" value={money(totals.pending)} />
        <StatCard label="Lifetime Paid" value={money(totals.paid)} />
        <StatCard label="Lifetime Earnings" value={money(totals.earnings)} />
      </div>
      <div className="business-card-grid two">{titles.map((title) => <article className="business-card" key={title.id}><span className="business-kicker">{title.title}</span><h2>{money(title.available_balance)} available</h2><div className="portal-mini-stats"><span>Pending <strong>{money(title.pending_payout)}</strong></span><span>Paid <strong>{money(title.lifetime_paid)}</strong></span></div><p>Lifetime earnings: {money(title.lifetime_earnings ?? title.total_revenue)}</p><button className="business-primary" type="button" disabled={!portal.data?.tax?.ready || busy === title.id || Number(title.available_balance || 0) < 100} onClick={() => request(title)}>{!portal.data?.tax?.ready ? 'W-9 verification required' : busy === title.id ? 'Submitting…' : Number(title.available_balance || 0) >= 100 ? 'Request Payout' : 'Minimum $100'}</button></article>)}</div>
      <div className="business-card portal-table-card"><h2>Payment History</h2>{allPayouts.length ? <div className="portal-table-wrap"><table className="portal-table"><thead><tr><th>Title</th><th>Amount</th><th>Status</th><th>Requested</th><th>Paid</th><th>Reference</th></tr></thead><tbody>{allPayouts.map(({ title, payout }, index) => <tr key={payout.id || `${title.id}-${index}`}><td>{title.title}</td><td>{money(payout.amount_requested ?? payout.amount)}</td><td>{statusLabel(payout.status)}</td><td>{dateLabel(payout.requested_at || payout.created_at)}</td><td>{dateLabel(payout.paid_at || payout.processed_at)}</td><td>{payout.payment_reference || '—'}</td></tr>)}</tbody></table></div> : <p>No payout requests are on file.</p>}</div>
    </PortalShell>
  );
}

function Delivery({ portal }) {
  const titles = portal.data?.titles || [];
  const [selected, setSelected] = useState(titles[0]?.id || '');
  const [status, setStatus] = useState(''); const [error, setError] = useState(''); const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const uploadInFlight = useRef(false);
  useEffect(() => { if (!selected && titles[0]?.id) setSelected(titles[0].id); }, [titles, selected]);

  async function saveLinks(event) {
    event.preventDefault(); setError(''); setSuccess(''); setStatus('Saving delivery links…'); const body = Object.fromEntries(new FormData(event.currentTarget).entries()); body.titleId = selected;
    try { const result = await filmmakerRequest('delivery-link', { session: portal.session, method: 'POST', body }); setStatus(''); setSuccess(result.message || 'Delivery links saved.'); await portal.refresh(); }
    catch (err) { setStatus(''); setError(err?.message || 'Delivery links could not be saved.'); }
  }

  async function upload(event) {
    event.preventDefault();
    if (uploadInFlight.current) return;

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const file = form.get('file');
    const kind = String(form.get('kind') || '');

    uploadInFlight.current = true;
    setUploading(true);
    setError('');
    setSuccess('');
    setStatus('Uploading material…');

    try {
      const result = await filmmakerUpload({ session: portal.session, titleId: selected, kind, file });
      if (!result?.url) throw new Error('Upload completed without a file URL.');
      formElement.reset();
      setSuccess(`${statusLabel(kind)} uploaded successfully.`);
      await portal.refresh();
    } catch (err) {
      setError(err?.message || 'Material upload failed.');
    } finally {
      setStatus('');
      uploadInFlight.current = false;
      setUploading(false);
    }
  }

  return (
    <PortalShell title="Title Delivery" subtitle="Send master video through Google Drive and upload supporting materials to the title you own.">
      {!titles.length ? <p className="business-message">An approved filmmaker title is required before delivery materials can be attached.</p> : <>
        <label className="portal-title-select">Title<select value={selected} onChange={(event) => setSelected(event.target.value)}>{titles.map((title) => <option key={title.id} value={title.id}>{title.title}</option>)}</select></label>
        <PortalMessage error={error} success={success} status={status} />
        <div className="business-card-grid two">
          <form className="business-form" onSubmit={saveLinks}><h2>Master delivery links</h2><label>Google Drive master video URL<input name="master_video_drive_url" type="url" placeholder="https://drive.google.com/…" /></label><label>Google Drive delivery folder URL<input name="delivery_drive_folder_url" type="url" placeholder="https://drive.google.com/…" /></label><label>Delivery notes<textarea name="delivery_notes" rows="4" /></label><button className="business-primary button" type="submit">Save Delivery Links</button></form>
          <form className="business-form" onSubmit={upload} aria-busy={uploading}><h2>Supporting material upload</h2><label>Material type<select name="kind" defaultValue="trailer"><option value="trailer">Trailer</option><option value="hero_image">Hero image</option><option value="poster">Poster</option><option value="title_logo">Title logo</option><option value="captions">Captions</option><option value="music_rights">Music rights</option><option value="vox_stems">Vocal stems</option><option value="music_stems">Music stems</option><option value="fx_stems">FX stems</option><option value="other">Other</option></select></label><label>File<input name="file" type="file" required /></label><p className="business-fineprint">Master video files are intentionally not uploaded here; use the Google Drive delivery link.</p><button className="business-primary button" type="submit" disabled={uploading}>{uploading ? 'Uploading…' : 'Upload Material'}</button></form>
        </div>
      </>}
    </PortalShell>
  );
}

function Messages({ portal }) {
  const [messages, setMessages] = useState([]); const [loading, setLoading] = useState(true); const [error, setError] = useState(''); const [success, setSuccess] = useState(''); const [sending, setSending] = useState(false);
  const load = useCallback(async () => { setLoading(true); setError(''); try { const result = await filmmakerRequest('messages', { session: portal.session }); setMessages(result.messages || []); } catch (err) { setError(err?.message || 'Messages could not be loaded.'); } finally { setLoading(false); } }, [portal.session?.access_token]);
  useEffect(() => { void load(); }, [load]);
  async function send(event) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setSending(true);
    setError('');
    setSuccess('');
    const body = Object.fromEntries(new FormData(formElement).entries());
    try {
      await filmmakerRequest('messages', { session: portal.session, method: 'POST', body });
      setSuccess('Message sent to WatchVIM.');
      formElement.reset();
      await load();
    } catch (err) {
      setError(err?.message || 'Message could not be sent.');
    } finally {
      setSending(false);
    }
  }
  return (
    <PortalShell title="Filmmaker Messages" subtitle="Keep title and distribution communication attached to your authenticated filmmaker account.">
      <PortalMessage error={error} success={success} status={loading ? 'Loading messages…' : ''} />
      <div className="business-two-column">
        <div className="portal-stack">{messages.length ? messages.map((message) => <article className="business-card portal-message-card" key={message.id || `${message.created_at}-${message.subject}`}><div className="portal-card-heading"><div><span className="business-kicker">{statusLabel(message.sender_role || 'WatchVIM')}</span><h2>{message.subject}</h2></div><span>{String(message.created_at || '').slice(0, 10)}</span></div><p className="business-preline">{message.body}</p></article>) : !loading ? <div className="business-card"><h2>No messages yet</h2><p>Use the form to contact the WatchVIM filmmaker team.</p></div> : null}</div>
        <form className="business-form sticky-card" onSubmit={send}><h2>New message</h2><label>Subject<input name="subject" required maxLength="180" /></label><label>Related title<select name="titleId" defaultValue=""><option value="">General question</option>{(portal.data?.titles || []).map((title) => <option key={title.id} value={title.id}>{title.title}</option>)}</select></label><label>Message<textarea name="body" rows="8" required maxLength="4000" /></label><button className="business-primary button" type="submit" disabled={sending}>{sending ? 'Sending…' : 'Send Message'}</button></form>
      </div>
    </PortalShell>
  );
}

function Orders({ portal }) {
  const titles = portal.data?.titles || [];
  const active = titles.filter((title) => title.ingest_status || title.delivery_status || title.qc_status || title.delivery_drive_folder_url || title.master_video_drive_url);
  return (
    <PortalShell title="Creative & Delivery Orders" subtitle="Track title delivery and ingest work currently associated with your filmmaker account.">
      {active.length ? <div className="business-card-grid two">{active.map((title) => <article className="business-card" key={title.id}><span className="business-kicker">{statusLabel(title.ingest_status || title.delivery_status || title.qc_status || 'Delivery')}</span><h2>{title.title}</h2><p>{title.delivery_notes || 'WatchVIM delivery materials are attached to this title.'}</p><NavLink to="/filmmakers/uploads">Manage Delivery</NavLink></article>)}</div> : <div className="business-card"><h2>No active delivery orders</h2><p>When WatchVIM requests delivery or ingest materials for an approved title, the title’s status will appear here.</p><NavLink to="/filmmakers/messages">Message WatchVIM</NavLink></div>}
    </PortalShell>
  );
}

function Profile({ portal }) {
  const metadata = portal.user?.user_metadata || {};
  return (
    <PortalShell title="Filmmaker Profile" subtitle="Account identity used to match submissions, title ownership, messages, revenue, and payouts.">
      <div className="business-card-grid two"><article className="business-card"><span className="business-kicker">Account Email</span><h2>{portal.user?.email || portal.data?.user?.email || '—'}</h2><p>This signed-in email is used when matching legacy submissions and owned filmmaker titles.</p></article><article className="business-card"><span className="business-kicker">Filmmaker Name</span><h2>{metadata.full_name || metadata.name || portal.user?.email?.split('@')[0] || 'Filmmaker'}</h2><p>Filmmaker portal access is isolated from WatchVIM consumer membership entitlements.</p></article></div>
    </PortalShell>
  );
}

export function FilmmakerPortalPage({ section = 'dashboard' }) {
  const portal = usePortalData();
  const content = useMemo(() => {
    if (section === 'titles') return <Titles portal={portal} />;
    if (section === 'uploads') return <Delivery portal={portal} />;
    if (section === 'revenue') return <Revenue portal={portal} />;
    if (section === 'payouts') return <Payouts portal={portal} />;
    if (section === 'messages') return <Messages portal={portal} />;
    if (section === 'orders') return <Orders portal={portal} />;
    if (section === 'profile') return <Profile portal={portal} />;
    return <Dashboard portal={portal} />;
  }, [section, portal.data, portal.error, portal.loading, portal.session?.access_token, portal.user?.id]);
  return <PortalGate portal={portal}>
    {portal.data?.titles?.length ? <section className="business-card" aria-label="Film-sales tax documents">
      <h2>{portal.data?.tax?.ready ? 'W-9 verified' : 'W-9 required for film-sales payments'}</h2>
      <p>{portal.data?.tax?.status === 'submitted' ? 'Your W-9 is awaiting VIM Finance review. Payouts unlock after verification.' : portal.data?.tax?.ready ? 'Your current W-9 has been reviewed by VIM Finance.' : 'U.S. payees must complete a signed W-9 in VIM Pay before requesting or receiving payment. Your earnings remain on your account.'}</p>
      {!portal.data?.tax?.ready ? <p>Status: {statusLabel(portal.data?.tax?.status || 'unavailable')}</p> : null}
      <a className="business-primary button" href="/pay/onboarding">{portal.data?.tax?.ready ? 'Manage tax documents' : 'Open secure W-9 onboarding'}</a>
      <p className="business-fineprint">Non-U.S. payee? Contact payroll@watchvim.us for the appropriate W-8 review. Do not email your SSN, EIN, or tax forms. Year-end tax forms are issued where required.</p>
    </section> : null}
    {content}
  </PortalGate>;
}

